import google, { mapContainerWidth, mutators, verify } from "@churchofjesuschrist/maps-google";
import { breakSizeDefault } from "@churchofjesuschrist/maps-constants";
import { controlClass, defaultEmpty, proxy, removeListeners, removeUndefined, styleClass } from "@churchofjesuschrist/maps-utils";
import MapTypeControl, { MapTypeControlStyle } from "@churchofjesuschrist/maps-google-map-type-control";
import GeolocateControl from "@churchofjesuschrist/maps-google-geolocate-control";
import ZoomControl from "@churchofjesuschrist/maps-google-zoom-control";
import LegalControl from "@churchofjesuschrist/maps-google-legal-control";
let initialized = false;
class DefaultControls {
  constructor(options = {}) {
    if (!initialized) {
      verify();
      construct();
      initialized = true;
    }
    this.setOptions(removeUndefined(options));
  }
}
const construct = () => {
  const controlsClass = styleClass("google-controls", null, controlClass())`
        margin-top: 0;
    `;
  const mapTypeOffsetClass = styleClass("google-controls", "maptype", controlsClass)`
        transform: translate(-50px, 90px);
    `;
  const geolocateOffsetClass = styleClass("google-controls", "geolocate", controlsClass)`
        transform: translateY(90px);
    `;
  const forwardEvents = (context, control, prefix, events) => {
    return events.map(event => {
      return control.addListener(event, (...args) => {
        let newEvent = prefix === event ? event : `${prefix}_${event}`;
        google.maps.event.trigger(context, newEvent, ...args);
      });
    });
  };
  Object.setPrototypeOf(DefaultControls.prototype, Object.assign(new google.maps.MVCObject(), mutators("map"), (() => {
    function onAdd() {
      let map = this.getMap();
      if (this.$map !== map) {
        onRemove.call(this);
        this.$map = map;
        this.$listeners = [map.addListener("resize", proxy(this.refresh, this))];
        if (window.ResizeObserver) {
          this.$observer = new ResizeObserver(proxy(this.refresh, this));
          this.$observer.observe(map.getDiv());
        }
        this.refresh();
      }
    }
    function onRemove() {
      if (this.$zoom) {
        this.$zoom.setMap(null);
        delete this.$zoom;
      }
      if (this.$maptype) {
        this.$maptype.setMap(null);
        delete this.$maptype;
      }
      if (this.$geolocate) {
        this.$geolocate.setMap(null);
        delete this.$geolocate;
      }
      if (this.$legal) {
        this.$legal.setMap(null);
        delete this.$legal;
      }
      if (removeListeners(this.$listeners)) {
        delete this.$listeners;
      }
      if (this.$observer) {
        this.$observer.disconnect();
        delete this.$observer;
      }
      delete this.$mode;
      delete this.$map;
    }
    function isTouchEnabled() {
      return "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
    }
    return {
      refresh: function () {
        let map = this.getMap();
        if (map) {
          let mapTypeControl = defaultEmpty(this.get("mapTypeControl"), true),
            mapTypeControlOptions = this.get("mapTypeControlOptions") || {},
            mapWidth = mapContainerWidth(map),
            mobileTouch = mapWidth <= breakSizeDefault && isTouchEnabled(),
            zoomControl = defaultEmpty(this.get("zoomControl"), !mobileTouch),
            geolocateControl = defaultEmpty(this.get("geolocateControl"), true),
            legalControl = defaultEmpty(this.get("legalControl"), true),
            legalControlOptions = this.get("legalControlOptions") || {};
          if (zoomControl && !this.$zoom) {
            this.$zoom = new ZoomControl({
              className: controlsClass
            });
          }
          if (mapTypeControl && !this.$maptype) {
            this.$maptype = new MapTypeControl({
              defaultMapTypeIds: mapTypeControlOptions.defaultMapTypeIds
            });
            let event = this.$maptype.addListener("mode_changed", proxy(this.refresh, this));
            this.$listeners.push(event);
          }
          if (geolocateControl && !this.$geolocate) {
            this.$geolocate = new GeolocateControl();
            let forwardedEvents = forwardEvents(this, this.$geolocate, "geolocate", ["geolocate", "success", "error"]);
            this.$listeners.push(...forwardedEvents);
          }
          if (legalControl && !this.$legal) {
            this.$legal = new LegalControl(legalControlOptions);
          }
          let offsetControls = mapTypeControl && zoomControl && (!mobileTouch && mapTypeControlOptions.style !== MapTypeControlStyle.BUTTON || mapTypeControlOptions.style === MapTypeControlStyle.OVERVIEW);
          let mode = [zoomControl, mapTypeControl, geolocateControl, legalControl, offsetControls, this.$maptype && this.$maptype.getMode() === MapTypeControlStyle.OVERVIEW].map(state => state ? "1" : "0").join("");
          if (mode !== this.$mode) {
            if (zoomControl) {
              this.$zoom.setMap(null);
            }
            if (mapTypeControl) {
              this.$maptype.setMap(null);
            }
            if (geolocateControl) {
              this.$geolocate.setMap(null);
            }
            if (legalControl) {
              this.$legal.setMap(null);
            }
            if (this.$zoom) {
              this.$zoom.setOptions({
                map: zoomControl ? map : null
              });
            }
            if (this.$maptype) {
              this.$maptype.setOptions(removeUndefined({
                className: offsetControls ? mapTypeOffsetClass : controlsClass,
                mapTypeIds: mapTypeControlOptions.mapTypeIds,
                style: mapTypeControlOptions.style,
                map: mapTypeControl ? map : null
              }));
            }
            if (this.$geolocate) {
              this.$geolocate.setOptions({
                className: offsetControls ? geolocateOffsetClass : controlsClass,
                map: geolocateControl ? map : null
              });
            }
            if (this.$legal) {
              this.$legal.setOptions({
                map: legalControl ? map : null
              });
            }
            this.$mode = mode;
            if (this.$mode) {
              google.maps.event.trigger(this, "mode_changed");
            }
          }
        }
      },
      geolocate: function () {
        return this.$geolocate?.geolocate();
      },
      changed: function (key) {
        switch (key) {
          case "map":
            if (this.getMap()) {
              onAdd.call(this);
            } else {
              onRemove.call(this);
            }
            break;
          case "mapTypeControl":
          case "zoomControl":
          case "geolocateControl":
          case "legalControl":
          case "legalControlOptions":
            this.refresh();
            break;
          case "mapTypeControlOptions":
            {
              let options = this.get("mapTypeControlOptions");
              if (options && this.$maptype) {
                this.$maptype.setOptions(options);
              }
              break;
            }
        }
      }
    };
  })()));
};
export default DefaultControls;
export { MapTypeControlStyle };